import React, { useState } from 'react';
import {
    Upload,
    Button,
    Form,
    Select,
    Input,
    List,
    Typography,
    Space,
    Popconfirm,
    message,
    Tag,
} from 'antd';
import { UploadOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import axios from 'axios';
import {
    CollateralDocument,
    DOCUMENT_TYPES,
} from '../types/collateral.types';
import { useCollateralDetail, useCollateralMutations } from '../hooks/useCollaterals';

const { Text } = Typography;

interface UploadedFileResponse {
    id: string;
    originalFileName: string;
    relativePath?: string;
    extension?: string;
}

interface CollateralDocumentUploadProps {
    collateralId: number;
}

/**
 * Upload supporting documents for a collateral (title deed, valuation report,
 * photos, etc.). Two-step flow: upload file to /api/files/upload, then attach
 * metadata via POST /api/collaterals/{id}/documents.
 */
const CollateralDocumentUpload: React.FC<CollateralDocumentUploadProps> = ({
    collateralId,
}) => {
    const [form] = Form.useForm<{ documentType: string; notes?: string }>();
    const [uploading, setUploading] = useState(false);
    const { data: detail, isLoading } = useCollateralDetail(collateralId);
    const { addDocument, removeDocument } = useCollateralMutations();

    const documents = detail?.documents ?? [];

    const handleUpload = async (file: RcFile): Promise<boolean> => {
        const values = await form.validateFields().catch(() => null);
        if (!values) {
            message.error('Select a document type first');
            return false;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append(
                'metadata',
                JSON.stringify({
                    category: 'COLLATERAL',
                    domain: 'COLLATERAL',
                    documentType: values.documentType,
                    description: `Collateral ${collateralId} — ${values.documentType}`,
                }),
            );

            const { data } = await axios.post<UploadedFileResponse>(
                '/api/files/upload',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );

            const doc: CollateralDocument = {
                documentType: values.documentType,
                documentName: values.documentType,
                fileId: data.id,
                fileName: data.originalFileName || file.name,
                mimeType: file.type,
                fileSize: file.size,
                notes: values.notes,
            };

            await addDocument.mutateAsync({ collateralId, doc });
            form.resetFields();
        } catch (err) {
            console.error('Upload failed', err);
            message.error('Failed to upload document');
        } finally {
            setUploading(false);
        }
        // antd Upload: return false to prevent it from keeping its own file list
        return false;
    };

    const handlePreview = (fileId: string) => {
        window.open(`/api/files/preview/documents/${fileId}`, '_blank');
    };

    return (
        <div>
            <Form form={form} layout="vertical">
                <Space direction="horizontal" align="end" style={{ width: '100%' }}>
                    <Form.Item
                        name="documentType"
                        label="Document Type"
                        rules={[{ required: true, message: 'Select a type' }]}
                        style={{ minWidth: 220 }}
                    >
                        <Select
                            placeholder="Select type"
                            options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
                        />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes" style={{ flex: 1, minWidth: 260 }}>
                        <Input placeholder="Optional notes" />
                    </Form.Item>
                    <Form.Item>
                        <Upload
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        >
                            <Button icon={<UploadOutlined />} loading={uploading}>
                                Upload
                            </Button>
                        </Upload>
                    </Form.Item>
                </Space>
            </Form>

            <List
                loading={isLoading}
                header={<Text strong>Attached Documents ({documents.length})</Text>}
                dataSource={documents}
                locale={{ emptyText: 'No documents uploaded yet' }}
                renderItem={(doc) => (
                    <List.Item
                        actions={[
                            <Button
                                key="preview"
                                type="link"
                                onClick={() => handlePreview(doc.fileId)}
                            >
                                Preview
                            </Button>,
                            <Popconfirm
                                key="delete"
                                title="Remove this document?"
                                okText="Remove"
                                okButtonProps={{ danger: true }}
                                onConfirm={() =>
                                    doc.id &&
                                    removeDocument.mutate({
                                        collateralId,
                                        documentId: doc.id,
                                    })
                                }
                            >
                                <Button type="link" danger icon={<DeleteOutlined />}>
                                    Remove
                                </Button>
                            </Popconfirm>,
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<FileOutlined style={{ fontSize: 20 }} />}
                            title={
                                <Space>
                                    <Text>{doc.fileName || doc.documentName || 'document'}</Text>
                                    <Tag color="blue">{doc.documentType}</Tag>
                                </Space>
                            }
                            description={
                                <Space split="·">
                                    {doc.uploadedBy && (
                                        <Text type="secondary">{doc.uploadedBy}</Text>
                                    )}
                                    {doc.uploadedAt && (
                                        <Text type="secondary">
                                            {new Date(doc.uploadedAt).toLocaleString()}
                                        </Text>
                                    )}
                                    {doc.notes && <Text type="secondary">{doc.notes}</Text>}
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />
        </div>
    );
};

export default CollateralDocumentUpload;
